import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import '../../assets/css/import.scss';
import Utilities from '../../assets/js/Utilities';
import api from '../../utils/api';
import PageTitle from '../UC/PageTitle';
import ImportImages from './ImportImages';
import ImportMeta from './ImportMeta';
import { useGlobalPopup } from "../../contexts/GlobalPopupContext";
import { useTranslation } from "../../contexts/TranslationContext";
import dayjs from 'dayjs';

// Function to convert DateOfIncidentTicks to dayjs object for LocalizationProvider
const convertTicksToDateTimeForPicker = (ticks) => {
  if (!ticks || ticks === 0) {
    console.log('convertTicksToDateTimeForPicker: No ticks provided');
    return null; // Return null for empty/invalid ticks
  }

  try {
    console.log('convertTicksToDateTimeForPicker: Converting ticks:', ticks);

    // Convert .NET ticks to JavaScript Date
    // .NET ticks are 100-nanosecond intervals since January 1, 0001 UTC
    // JavaScript Date uses milliseconds since January 1, 1970 UTC
    // .NET DateTime.Ticks for Unix epoch (1970-01-01) = 621355968000000000
    const dotNetTicksAtUnixEpoch = 621355968000000000;
    const ticksPerMillisecond = 10000;

    // Convert to milliseconds since Unix epoch
    const milliseconds = (ticks - dotNetTicksAtUnixEpoch) / ticksPerMillisecond;
    console.log('convertTicksToDateTimeForPicker: Calculated milliseconds:', milliseconds);

    // Validate the calculated milliseconds
    if (milliseconds < 0 || !isFinite(milliseconds)) {
      console.error('convertTicksToDateTimeForPicker: Invalid milliseconds calculated:', milliseconds);
      return null;
    }

    // Create JavaScript Date and convert to dayjs
    const jsDate = new Date(milliseconds);
    console.log('convertTicksToDateTimeForPicker: Created JavaScript Date:', jsDate);

    // Validate the JavaScript Date
    if (isNaN(jsDate.getTime())) {
      console.error('convertTicksToDateTimeForPicker: Invalid JavaScript Date created');
      return null;
    }

    const dayjsObj = dayjs(jsDate);
    console.log('convertTicksToDateTimeForPicker: Created dayjs object:', dayjsObj.format());

    // Validate the dayjs object
    if (!dayjsObj.isValid()) {
      console.error('convertTicksToDateTimeForPicker: Invalid dayjs object created');
      return null;
    }

    return dayjsObj;
  } catch (error) {
    console.error('Error converting ticks to dayjs:', error);
    return null;
  }
};

// Function to convert URL to base64 or return URL directly
const GetBase64FromUrl = (url) => {
  // Return early if URL is empty or already base64
  if (!url || url.startsWith('data:')) {
    return url || '';
  }

  // For URLs, return the URL directly for immediate use
  // The actual base64 conversion can be done later if needed
  return url;
};

const Import = ({ isModal = false, editId: propEditId = null, onClose = null, onEditSuccess = null, arrAccounts = [] }) => {

  const navigate = useNavigate();

  const [images, setImages] = useState([]); // All images with isExisting and isRemove flags
  const [files, setFiles] = useState([]);
  const [arrImageIDs_toRemove, setArrImageIDs_toRemove] = useState([]); // IDs of images to remove from backend
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [formInput, setFormInput] = useState({});
  const [isImporting, setIsImporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editId, setEditId] = useState(propEditId);
  const [isEditMode, setIsEditMode] = useState(!!propEditId);
  const { showPopup } = useGlobalPopup();
  const { language } = useTranslation();

  const svgIconImport = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 7.757v8.486M7.757 12H16.243M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );

  // Track if user data has been initialized to prevent re-initialization
  const userDataInitialized = useRef(false);

  // Memoized setFormInput to prevent child re-renders
  const memoizedSetFormInput = useCallback((update) => {
    if (typeof update === 'function') {
      setFormInput(update);
    } else {
      setFormInput(update);
    }
  }, []);

  // Memoized setImages to prevent child re-renders
  const memoizedSetImages = useCallback((update) => {
    if (typeof update === 'function') {
      setImages(update);
    } else {
      setImages(update);
    }
  }, []);

  // Get ID from URL parameters or search params (only if not in modal mode)
  const [searchParams] = useSearchParams();
  const { id } = useParams();

  useEffect(() => {
    // In modal mode, use propEditId; otherwise check URL
    if (isModal && propEditId) {

      setEditId(propEditId);
      setIsEditMode(true);
      loadExistingGallery(propEditId);
    } else if (!isModal) {
      // Check for edit ID from URL params or search params
      const editIdFromUrl = id || searchParams.get('id');
      if (editIdFromUrl) {
        setEditId(editIdFromUrl);
        setIsEditMode(true);
        loadExistingGallery(editIdFromUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchParams, isModal, propEditId]);

  // Initialize form data for new cases - only run once when component mounts
  useEffect(() => {
    // Only initialize if not in edit mode and not already initialized
    if (!isEditMode && !userDataInitialized.current) {
      try {
        const userDataString = localStorage.getItem("FWUser");
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          const userInfo = userData.Information;

          if (userInfo) {
            setFormInput(prevInput => ({
              ...prevInput,
              EnteredBy: userInfo.Name || '',
              Phone: userInfo.Phone || '',
              Email: userInfo.Email || '',
              Agency: userInfo.AgencyName || ''
            }));
            userDataInitialized.current = true;
          }
        }
      } catch (error) {
        console.error('Error loading user information from localStorage:', error);
      }
    }
  }, [isEditMode]); // Only depend on isEditMode

  const defineCategory = {
    NewShoe: 1,
    RecoveredShoe: 2,
    TestShoe: 3
  };

  // Function to load existing gallery data for editing
  const loadExistingGallery = async (galleryId) => {


    setIsLoading(true);

    try {
      const response = await api.post(`${global.config.api.domain}/fwiq-api/api/evidence/search`, {
        PageNumber: 1,
        PageSize: 1,
        IDs: [galleryId]
      });

      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = response.data;

      if (result && result.Records && result.Records.length > 0) {
        const galleryData = result.Records[0];

        // Debug logging to understand the data structure
        console.log('Loaded gallery data:', galleryData);
        console.log('Gallery data keys:', Object.keys(galleryData));

        let timeZoneOffset = new Date().getTimezoneOffset();

        let ownerOfThisGallery = arrAccounts.find(account => account.User_ID === galleryData.User_ID && account.User_ID_Ext === galleryData.User_ID_Ext);
        let agencyOfOwner = arrAccounts.find(account => account.User_ID === galleryData.User_ID && account.User_ID_Ext === 0);

        // Ensure proper category value - convert to number and validate
        const categoryValue = parseInt(galleryData.Category_ID) || 0;
        console.log('Loading gallery with Category_ID:', galleryData.Category_ID, 'parsed as:', categoryValue);
        console.log('defineCategory values:', defineCategory);
        console.log('Is TestShoe?', categoryValue === defineCategory.TestShoe);

        // DateTime value - convert ticks directly to dayjs object for the DatePicker component
        let dateOfIncidentDayjs = null;
        if (galleryData.ImpressionInfo?.DateOfIncidentTicks) {
          console.log('Converting DateOfIncidentTicks:', galleryData.ImpressionInfo.DateOfIncidentTicks);

          // Try the custom ticks conversion first
          dateOfIncidentDayjs = convertTicksToDateTimeForPicker(galleryData.ImpressionInfo.DateOfIncidentTicks);
          console.log('Converted to dayjs object:', dateOfIncidentDayjs);

          // Validate the conversion
          if (!dateOfIncidentDayjs || !dateOfIncidentDayjs.isValid()) {
            console.warn('Custom ticks conversion failed, trying Utilities.ConvertTicksToDateTime as fallback');

            // Fallback to Utilities conversion
            try {
              const jsDate = Utilities.ConvertTicksToDateTime(galleryData.ImpressionInfo.DateOfIncidentTicks);
              if (jsDate && jsDate instanceof Date && !isNaN(jsDate.getTime())) {
                dateOfIncidentDayjs = dayjs(jsDate);
                console.log('Fallback conversion successful:', dateOfIncidentDayjs.format());
              }
            } catch (fallbackError) {
              console.error('Fallback conversion also failed:', fallbackError);
            }

            // If still invalid, set to null
            if (!dateOfIncidentDayjs || !dateOfIncidentDayjs.isValid()) {
              console.error('All date conversion attempts failed, setting to null');
              dateOfIncidentDayjs = null;
            }
          }
        }

        // Load metadata into form
        setFormInput({
          // System timestamps
          CollectUtcTicks: Utilities.ConvertDateTimeToTicks(Utilities.GetUtcDate(new Date())),
          CollectLocalTicks: Utilities.ConvertDateTimeToTicks(new Date()),
          TimeZone: Utilities.GetTimeZoneStringByOffset(timeZoneOffset),
          TimeZoneOffset: timeZoneOffset,

          // ShoesInfo fields - map from flat API response structure
          Make: galleryData.Make || '',
          Model: galleryData.Model || '',
          ShoeType: galleryData.ShoeType || '',
          Category: categoryValue, // Ensure this is a proper number
          ProductCode: galleryData.ProductCode || '',
          Color: galleryData.Colors || '', // API field: "Colors" → Form field: "Color"
          ReferenceLink: galleryData.ReferenceLink || '',

          // ImpressionInfo fields - these are at root level in API response
          CaseIncident: galleryData.ImpressionInfo?.CaseIncident || '',
          CartridgeCaseNumber: galleryData.ImpressionInfo?.ImpressionNumber || '',
          Country: galleryData.ImpressionInfo?.Country_ID?.toString() || '',
          State: galleryData.ImpressionInfo?.State_ID?.toString() || '',
          County: galleryData.ImpressionInfo?.County_ID?.toString() || '',
          RecoveryEvent: galleryData.ImpressionInfo?.RecoveryEvent_ID?.toString() || '',
          EventType: galleryData.ImpressionInfo?.EventType_ID?.toString() || '',
          LocationIncident: galleryData.ImpressionInfo?.LocationOfIncident || '',
          LocationNote: galleryData.ImpressionInfo?.LocationNote || '',
          Latitude: galleryData.ImpressionInfo?.Latitude?.toString() || '',
          Longitude: galleryData.ImpressionInfo?.Longitude?.toString() || '',

          // Date handling - use converted dayjs object for LocalizationProvider
          DateOfIncident: dateOfIncidentDayjs,

          // Time handling - use TimeOfIncidentInSeconds from API if available, otherwise extract from date
          TimeOfIncident: galleryData.ImpressionInfo.TimeOfIncidentInSeconds,

          // ID fields for maintaining relationships during updates
          Make_ID: galleryData.Make_ID || '',
          Model_ID: galleryData.Model_ID || '',
          ShoeType_ID: galleryData.ShoeType_ID || '',

          // NotificationInfo fields - these may not exist in API response, use defaults
          CaseNotes: galleryData.NotificationInfo?.CaseNotes || '',
          InvestigatorAssigned: galleryData.NotificationInfo?.DetectiveInvestigatorAssigned || false,
          EnteredBy: ownerOfThisGallery?.Name || '',
          Phone: ownerOfThisGallery?.Phone || '',
          Email: ownerOfThisGallery?.Email || '',
          Agency: agencyOfOwner?.Name || '',
          InvestigatorAgency: galleryData.NotificationInfo?.Agency || '',
          InvestigatorName: galleryData.NotificationInfo?.Name || '',
          InvestigatorPhone: galleryData.NotificationInfo?.Phone || '',
          InvestigatorEmail: galleryData.NotificationInfo?.Email || '',
          EmailsForNotifications: galleryData.NotificationInfo?.EmailsForNotifications || ''
        });

        // Load images data
        if (galleryData.Images && galleryData.Images.length > 0) {

          let arrOriginalImages = galleryData.Images.filter(img => img.IsStandardize === 0);
          
          const loadedImages = arrOriginalImages.map((img, index) => {
            // Convert StandardizedRects back to our internal format
            let standardizedRect = null;

            // // Handle StandardizedRects format from the API
            // if (img.StandardizedRects && img.StandardizedRects.length > 0) {
            //   const rect = img.StandardizedRects[0];
            //   standardizedRect = {
            //     topLeft: { x: rect.TopLeft?.X || 0, y: rect.TopLeft?.Y || 0 },
            //     topRight: { x: rect.TopRight?.X || 0, y: rect.TopRight?.Y || 0 },
            //     bottomLeft: { x: rect.BottomLeft?.X || 0, y: rect.BottomLeft?.Y || 0 },
            //     bottomRight: { x: rect.BottomRight?.X || 0, y: rect.BottomRight?.Y || 0 }
            //   };
            // }

            //find the standardized image by matching img.IDValue with Original_IDValue in galleryData.Images
            let imgStandardized = galleryData.Images.find(stdImg => stdImg.Original_IDValue === img.IDValue);

            let standardizeBase64 = null;

            if (imgStandardized) {
              console.log(`Found standardized image for ${img.IDValue}:`, imgStandardized);

              // Parse JsonExtendInfo to get the standardized rect data
              let dataStandardizedRect = {};
              try {
                dataStandardizedRect = JSON.parse(imgStandardized.JsonExtendInfo || '{}');
              } catch (e) {
                console.warn('Failed to parse JsonExtendInfo:', e);
                dataStandardizedRect = {};
              }

              standardizedRect = dataStandardizedRect.Rect || null;

              // Use standardized base64 if available
              if (imgStandardized.StandardizedBase64) {
                standardizeBase64 = imgStandardized.StandardizedBase64.startsWith('data:') ?
                  imgStandardized.StandardizedBase64 :
                  `data:image/jpeg;base64,${imgStandardized.StandardizedBase64}`;
              }
              else if (imgStandardized.S3OriginalRelativePath) {
                let urlStandardized = '';
                if (imgStandardized.S3OriginalRelativePath.startsWith('/')) {
                  urlStandardized = `${global.config.images3.link}${imgStandardized.S3OriginalRelativePath}`;
                }
                else {
                  urlStandardized = `${global.config.images3.link}/${imgStandardized.S3OriginalRelativePath}`;
                }
                // Use the URL as standardized image source if no Base64 is available
                standardizeBase64 = urlStandardized;
              }
            } else {
              console.log(`No standardized image found for original image ${img.IDValue}`);
            }



            // Create proper image URLs from the API data
            // console.log(`Processing image ${index + 1}: ${img.Name || 'Unnamed'} (ID: ${img.IDValue})`);
            let urlOrigin = '';

            

            if (img.S3OriginalRelativePath) {
              if (img.S3OriginalRelativePath.startsWith('/')) {
                urlOrigin = `${global.config.images3.link}${img.S3OriginalRelativePath}`;
              }
              else {
                urlOrigin = `${global.config.images3.link}/${img.S3OriginalRelativePath}`;
              }
            }

            // For edit mode: use URL for thumbnails, urlOrigin for preview
            let thumbnailUrl = '';
            let previewUrl = '';

            if (img.Base64) {
              // If Base64 is available, use it for both thumbnail and preview as fallback
              const base64DataUrl = img.Base64.startsWith('data:') ?
                img.Base64 :
                `data:image/jpeg;base64,${img.Base64}`;
              thumbnailUrl = base64DataUrl;
              previewUrl = urlOrigin || base64DataUrl; // Prefer urlOrigin for preview
            } else {
              // Use URL paths from S3
              thumbnailUrl = img.Url ?
                (img.Url.startsWith('http') ? img.Url : `${global.config.images3.link}/${img.Url}`) : '';
              previewUrl = urlOrigin || thumbnailUrl; // Prefer urlOrigin for preview
            }

            // Handle captured time
            const capturedTime = img.CapturedTimeValue ?
              new Date(img.CapturedTimeValue).getTime() :
              (img.CapturedTicks ? img.CapturedTicks : Date.now());

            return {
              id: img.ID || img.IDValue || `img_${index}_${Date.now()}`,
              name: img.Name || `Image ${index + 1}`,
              size: img.Length || 0, // Use Length from ImagePositions if available
              base64: GetBase64FromUrl(thumbnailUrl), // Used for thumbnails
              originalUrl: previewUrl, // Used for preview/original image display
              standardizedBase64: standardizeBase64, // Will be set if standardized version exists
              standardizedImageId: imgStandardized ? (imgStandardized.IDValue || imgStandardized.ID) : null, // Store standardized image ID if available
              standardizedRect: standardizedRect, // Will be set if rect data exists
              type: img.Type ? img.Type.toString() : "0",
              url: img.Url || '',
              md5: img.Md5 || '',
              jsonExtendInfo: img.JsonExtendInfo || '',
              latitude: img.Latitude || 0,
              longitude: img.Longitude || 0,
              address: img.Address || '',
              ipAddress: img.IPAddress || '',
              capturedTicks: img.CapturedTicks || capturedTime,
              capturedTimeValue: img.CapturedTimeValue || '',
              isExisting: true, // Mark as existing image
              isRemove: false // Not marked for deletion
            };
          });

          // In edit mode: loaded images go to images array with isExisting flag
          setImages(loadedImages);
          setPreviewImageIndex(0);

          console.log(`Loaded ${loadedImages.length} existing images successfully`);
        } else {
          // No existing images
          setImages([]);
        }

        console.log('Gallery loaded successfully:', galleryData);
      } else {
        console.error('No gallery data found for ID:', galleryId);
        showPopup({
          type: 'error',
          title: language.import?.galleryNotFound || 'Gallery Not Found',
          content: language.import?.galleryNotFoundMessage || 'Gallery not found or no data available.',
          options: [{ text: language.import?.ok || 'OK', action: null, type: 'default' }],
          showCloseButton: true,
          backdrop: true
        });
      }
    } catch (error) {
      console.error('Error loading gallery:', error);
      showPopup({
        type: 'error',
        title: language.import?.loadingError || 'Loading Error',
        content: language.import?.loadingErrorMessage || 'Failed to load gallery data. Please try again.',
        options: [{ text: language.import?.ok || 'OK', action: null, type: 'default' }],
        showCloseButton: true,
        backdrop: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkMissingRequiredFields = () => {
    const missingRequiredFields = [];

    // Check main required fields
    if (!formInput.CaseIncident?.trim()) {
      missingRequiredFields.push(language?.import?.caseIncident || 'Case Incident#');
    }
    if (!formInput.CartridgeCaseNumber?.trim()) {
      missingRequiredFields.push(language?.import?.impressionEvidence || 'Impression Evidence #');
    }
    // Check DateOfIncident (always required)
    if (!formInput.DateOfIncident || !formInput.DateOfIncident.isValid()) {
      missingRequiredFields.push(language?.import?.datetimeIncident || 'Date/Time of Incident');
    }

    // // TimeOfIncident should be either -1 (unknown) or a valid number (seconds since midnight)
    // if (formInput.TimeOfIncident !== -1 && (formInput.TimeOfIncident === null || formInput.TimeOfIncident === undefined || typeof formInput.TimeOfIncident !== 'number')) {
    //   missingRequiredFields.push(language?.import?.datetimeIncident || 'Date/Time of Incident');
    // }
    if (!formInput.LocationIncident?.trim()) {
      missingRequiredFields.push(language?.import?.locationIncident || 'Location of Incident');
    }

    // if (!formInput.Country) {
    //   missingRequiredFields.push(language?.import?.country || 'Country');
    // }
    // if (!formInput.State) {
    //   missingRequiredFields.push(language?.import?.state || 'State');
    // }

    // if (!formInput.CaseNotes?.trim()) {
    //   missingRequiredFields.push(language?.import?.caseNotes || 'Case Notes');
    // }
    // if (!formInput.EnteredBy?.trim()) {
    //   missingRequiredFields.push(language?.import?.enteredBy || 'Entered By');
    // }
    // if (!formInput.Phone?.trim()) {
    //   missingRequiredFields.push(language?.import?.phone || 'Phone #');
    // }
    // if (!formInput.Email?.trim()) {
    //   missingRequiredFields.push(language?.import?.email || 'Email(s)');
    // }
    // if (!formInput.Agency?.trim()) {
    //   missingRequiredFields.push(language?.import?.agency || 'Agency');
    // }

    // Check conditional required fields based on category
    if (parseInt(formInput.Category) === defineCategory.TestShoe) {
      if (!formInput.Make?.trim()) {
        missingRequiredFields.push(language?.import?.make || 'Make');
      }
      if (!formInput.ShoeType?.trim()) {
        missingRequiredFields.push(language?.import?.shoeType || 'Shoe Type');
      }
      if (!formInput.Model?.trim()) {
        missingRequiredFields.push(language?.import?.model || 'Model');
      }
    }

    // Check investigator fields if InvestigatorAssigned is true
    if (formInput.InvestigatorAssigned) {
      if (!formInput.InvestigatorName?.trim()) {
        missingRequiredFields.push(language?.import?.name || 'Investigator Name');
      }
      if (!formInput.InvestigatorPhone?.trim()) {
        missingRequiredFields.push(language?.import?.phone || 'Investigator Phone #');
      }
      if (!formInput.InvestigatorEmail?.trim()) {
        missingRequiredFields.push(language?.import?.emails || 'Investigator Email(s)');
      }
      if (!formInput.InvestigatorAgency?.trim()) {
        missingRequiredFields.push(language?.import?.agency || 'Investigator Agency');
      }
      if (!formInput.EmailsForNotifications?.trim()) {
        missingRequiredFields.push(language?.import?.EmailsForNotifications || 'Investigator Agency');
      }
    }

    return missingRequiredFields;
  }

  const ImportGallery = async () => {
   
    // Validate that we have images (not marked for removal)
    const totalImageCount = images.filter(img => !img.isRemove).length;
    if (totalImageCount === 0) {
      showPopup({
        type: 'warning',
        title: language.import?.missingImages || 'Missing Images',
        content: language.import?.missingImagesMessage || 'Please add at least one image before importing.',
        options: [{ text: language.import?.ok || 'OK', action: null, type: 'default' }],
        showCloseButton: true,
        backdrop: true
      });
      return;
    }

    // Validate that all NEW images have a type selected (existing images already validated)
    const newImagesWithoutType = images.filter(img => !img.isExisting && !img.isRemove && (!img.type || img.type === "0"));
    if (newImagesWithoutType.length > 0) {
      showPopup({
        type: 'warning',
        title: language.import?.missingImageTypes || 'Missing Image Types',
        content: language.import?.missingImageTypesMessage || 'Please select a type for all new images before importing.',
        options: [{ text: language.import?.ok || 'OK', action: null, type: 'default' }],
        showCloseButton: true,
        backdrop: true
      });
      return;
    }

    // Validate that non-Other NEW image types have standardized images
    const newImagesNeedingStandardization = images.filter(img =>
      !img.isExisting &&
      !img.isRemove &&
      img.type &&
      img.type !== "0" &&
      img.type !== global.config.Defines.ImageTypeKey.SHOE_OTHER?.toString() &&
      !img.standardizedBase64
    );

    if (newImagesNeedingStandardization.length > 0) {
      const imageNames = newImagesNeedingStandardization.map(img => img.name).join(', ');
      showPopup({
        type: 'warning',
        title: language.import?.missingStandardizedImages || 'Missing Standardized Images',
        content: language.import?.missingStandardizedImagesMessage?.replace('{imageNames}', imageNames) ||
          `The following new images require standardization: ${imageNames}. Please use Auto Standardize or Manual Standardize before importing.`,
        options: [{ text: language.import?.ok || 'OK', action: null, type: 'default' }],
        showCloseButton: true,
        backdrop: true
      });
      return;
    }

     // Validate ImportMeta required fields first
    const missingRequiredFields = checkMissingRequiredFields();


    // Show popup warning if any required fields are missing
    if (missingRequiredFields.length > 0) {
      const fieldListHtml = `
        <div class="missing-fields-container">
          <p>The following required fields are missing:</p>
          <ul class="missing-fields-list">
            ${missingRequiredFields.map(field => `<li>${field}</li>`).join('')}
          </ul>
          <p>Please fill in all required fields before importing.</p>
        </div>
      `;
      showPopup({
        type: 'warning',
        title: language?.import?.missingRequiredFields || 'Missing Required Fields',
        content: fieldListHtml,
        options: [{ text: language?.import?.ok || 'OK', action: null, type: 'default' }],
        showCloseButton: true,
        backdrop: true
      });
      return;
    }


    setIsImporting(true);

    try {


      // Prepare the upload data
      const uploadData = GetDataUploadFromInput();

      // Debug log to show what's being sent
      const deletedImages = images.filter(img => img.isRemove);
      console.log('Upload data for API:', {
        isEditMode,
        newImagesCount: images.filter(img => !img.isExisting && !img.isRemove).length,
        deletedImagesCount: deletedImages.length,
        deletedImageIds: deletedImages.map(img => img.id),
        hasDeleteImageIDs: !!uploadData.DeleteImage_IDs
      });

      // Determine API endpoint based on edit mode
      const urlUpload = isEditMode
        ? `${global.config.api.domain}/fwiq-api/api/evidence/update?id=${editId}`
        : `${global.config.api.domain}/fwiq-api/api/upload/add`;

      let bucket = 'catalog';
      if (parseInt(formInput.Category) !== defineCategory.NewShoe) {
        bucket = 'evidence';
      }

      // Add ID to upload data if editing
      if (isEditMode && editId) {
        uploadData.Id = editId;
      }

      const response = await api.post(urlUpload, uploadData, {
        headers: {
          'bucket': bucket,
          "fiq-client-type": "web"
        }
      });

      const result = response.data;

      if (result && result.Code !== undefined) {
        if (result.Code > 0) {
          // Import/Update failed
          console.error(`${isEditMode ? 'Update' : 'Import'} failed:`, result);
          showPopup({
            type: 'error',
            title: `${isEditMode ? 'Update' : 'Import'} Failed`,
            content: `${isEditMode ? 'Update' : 'Import'} failed: ${result.Message || 'Unknown error occurred'}`,
            options: [{ text: 'OK', action: null, type: 'default' }],
            showCloseButton: true,
            backdrop: true
          });
        } else {
          // Import/Update successful
          console.log(`${isEditMode ? 'Update' : 'Import'} successful:`, result);

          // Call success callback for edit operations
          if (isEditMode && onEditSuccess) {
            onEditSuccess();
          }

          // Handle modal mode vs page mode
          if (isModal && isEditMode && onClose) {
            // Show success message and close modal after successful update
            showPopup({
              type: 'success',
              title: 'Update Successful',
              content: 'Gallery updated successfully!',
              options: [{
                text: 'OK',
                action: () => onClose(),
                type: 'default'
              }],
              showCloseButton: true,
              backdrop: true
            });
          } else if (!isEditMode) {
            // Show success message and clear form for new imports
            showPopup({
              type: 'success',
              title: 'Import Successful',
              content: 'Gallery imported successfully!',
              options: [
                {
                  text: 'View in Evidence', action: () => {
                    navigate(`/evidence`);
                  }, type: 'primary'
                },
                { text: 'Import More', action: null, type: 'default' },
              ],
              showCloseButton: true,
              backdrop: true
            });

            // Clear the form for new imports
            setImages([]);
            setFiles([]);
            setPreviewImageIndex(0);
            setFormInput({});
          } else {
            // Edit mode but not modal
            showPopup({
              type: 'success',
              title: 'Update Successful',
              content: 'Gallery updated successfully!',
              options: [{ text: 'OK', action: null, type: 'default' }],
              showCloseButton: true,
              backdrop: true
            });
          }
        }
      } else {
        // Unexpected response format
        console.error('Unexpected response format:', result);
        showPopup({
          type: 'warning',
          title: 'Unexpected Response',
          content: `${isEditMode ? 'Update' : 'Import'} completed but received unexpected response format.`,
          options: [{ text: 'OK', action: null, type: 'default' }],
          showCloseButton: true,
          backdrop: true
        });
      }
    } catch (error) {
      console.error(`Error during ${isEditMode ? 'update' : 'import'}:`, error);
      showPopup({
        type: 'error',
        title: 'Operation Error',
        content: `An error occurred during ${isEditMode ? 'update' : 'import'}. Please try again.`,
        options: [{ text: 'OK', action: null, type: 'default' }],
        showCloseButton: true,
        backdrop: true
      });
    } finally {
      setIsImporting(false);
    }
  }

  const GetDataUploadFromInput = () => {

    // Convert only new images (not existing ones) to the format expected by the API
    // Filter out existing images and removed images
    const convertedImages = images.filter(img => !img.isExisting && !img.isRemove).map(img => {
      const imageData = {
        Name: img.name,
        Type: img.type ? img.type.toString() : "0",
        Base64: (() => {
          // Handle different image sources in edit mode
          if (img.base64.includes(',')) {
            // It's a data URL, extract base64 part
            return img.base64.split(',')[1];
          } else if (img.base64.startsWith('http')) {
            // It's a URL, we can't convert this to base64 here
            // For edit mode, the server should handle URL-based images
            console.warn(`Image ${img.name} is URL-based, server should handle this case`);
            return ''; // Empty base64, server will use existing image
          } else {
            // It's already base64 string
            return img.base64;
          }
        })(),
        // Md5: img.md5 || "", // Include MD5 if available from edit mode
        JsonExtendInfo: img.jsonExtendInfo || "",
        // StandardizeMode: img.standardizedBase64 ? "Standard" : "Ignore",
        CreatedTicks: Utilities.ConvertDateTimeToTicks(new Date()),
        CreatedTimestamp: Date.now(),
        CapturedTicks: img.capturedTicks || Utilities.ConvertDateTimeToTicks(new Date()),
        CapturedTimestamp: img.capturedTicks || Date.now(),
        Url: img.url || "", // Include original URL for edit mode
        // Latitude: parseFloat(img.latitude) || 0.0,
        // Longitude: parseFloat(img.longitude) || 0.0,
        // Address: img.address || "",
        IPAddress: img.ipAddress || "",
        // Add ID for existing images in edit mode
        ...(img.id && img.id.startsWith('img_') ? {} : { IDValue: img.id })
      };



      // Add StandardizedRects only for Shoe Print
      if (img.type !== global.config.Defines.ImageTypeKey.SHOE_OTHER){

        // || img.type === global.config.Defines.ImageTypeKey.SHOE_PRINT
        // || img.type === global.config.Defines.ImageTypeKey.SHOE_PRINT_LEFT
        // || img.type === global.config.Defines.ImageTypeKey.SHOE_PRINT_RIGHT
        // || img.type === global.config.Defines.ImageTypeKey.SHOE_PRINT_UNDETERMINED) {

        if (img.standardizedRect)

          imageData.StandardizedRects = [{
            TopLeft: {
              X: parseFloat(img.standardizedRect.TopLeft?.x || img.standardizedRect.TopLeft?.X || 0),
              Y: parseFloat(img.standardizedRect.TopLeft?.y || img.standardizedRect.TopLeft?.Y || 0)
            },
            TopRight: {
              X: parseFloat(img.standardizedRect.TopRight?.x || img.standardizedRect.TopRight?.X || 0),
              Y: parseFloat(img.standardizedRect.TopRight?.y || img.standardizedRect.TopRight?.Y || 0)
            },
            BottomLeft: {
              X: parseFloat(img.standardizedRect.BottomLeft?.x || img.standardizedRect.BottomLeft?.X || 0),
              Y: parseFloat(img.standardizedRect.BottomLeft?.y || img.standardizedRect.BottomLeft?.Y || 0)
            },
            BottomRight: {
              X: parseFloat(img.standardizedRect.BottomRight?.x || img.standardizedRect.BottomRight?.X || 0),
              Y: parseFloat(img.standardizedRect.BottomRight?.y || img.standardizedRect.BottomRight?.Y || 0)
            }
          }];
      }

      return imageData;
    });

    // @Na 2025-09-22: Note***
    // Cannot edit standardized images or remove standardized images in edit mode for this current design of api
    // User must remove this original image, then re-upload a new image to get a new standardized image

    const arrDeleteImage_ID = images.filter(img => img.isRemove).map(img => img.id);

    let utcDateOfIncident = (formInput.DateOfIncident ? new Date(formInput.DateOfIncident) : new Date());
    // default add time to 12:00:00 of utcDateOfIncident
    utcDateOfIncident.setUTCHours(12, 0, 0, 0);

    return {
      CollectUtcTicks: Utilities.ConvertDateTimeToTicks(Utilities.GetUtcDate(new Date())),
      CollectLocalTicks: Utilities.ConvertDateTimeToTicks(new Date()),
      ShoesInfo: {
        Make: formInput.Make || '',
        Model: formInput.Model || '',
        ShoeType: formInput.ShoeType || '',
        Category_ID: formInput.Category || '',
        ProductCode: formInput.ProductCode || '',
        Color: formInput.Color || '',
        ReferenceLink: formInput.ReferenceLink || '',
        JsonExtendInfo: ""
      },
      Images: convertedImages,
      DeleteImage_IDs: arrImageIDs_toRemove, // IDs of images to delete in edit mode
      TimeZone: Utilities.GetTimeZoneStringByOffset(new Date().getTimezoneOffset()),
      TimeZoneOffset: new Date().getTimezoneOffset(),
      ImpressionInfo: {
        CaseIncident: formInput.CaseIncident || '',
        ImpressionNumber: formInput.CartridgeCaseNumber || '', // Map CartridgeCaseNumber to ImpressionNumber
        Country_ID: parseInt(formInput.Country) || 0,
        State_ID: parseInt(formInput.State) || 0,
        County_ID: parseInt(formInput.County) || 0,
        RecoveryEvent_ID: parseInt(formInput.RecoveryEvent) || 0,
        EventType_ID: parseInt(formInput.EventType) || 0,
        DateOfIncidentTicks: Utilities.ConvertDateTimeToTicks(utcDateOfIncident),
        LocalDateOfIncidentTicks: Utilities.ConvertDateTimeToTicks(formInput.DateOfIncident ? new Date(formInput.DateOfIncident) : new Date()),
        DateOfIncidentTimestamp: formInput.DateOfIncident ? new Date(formInput.DateOfIncident).getTime() : Date.now(),
        LocalDateOfIncidentTimestamp: formInput.DateOfIncident ? new Date(formInput.DateOfIncident).getTime() : Date.now(),
        TimeOfIncidentInSeconds: formInput.TimeOfIncident || 0,

        ScanTicks: Utilities.ConvertDateTimeToTicks(Utilities.GetUtcDate(new Date())),
        LocalScanTicks: Utilities.ConvertDateTimeToTicks(new Date()),
        ScanTimestamp: Date.now(),
        LocalScanTimestamp: Date.now(),
        LocationOfIncident: formInput.LocationIncident || '',
        Latitude: parseFloat(formInput.Latitude) || 0.0,
        Longitude: parseFloat(formInput.Longitude) || 0.0,
        LocationNote: formInput.LocationNote || ''
      },
      NotificationInfo: {
        CaseNotes: formInput.CaseNotes || '',
        DetectiveInvestigatorAssigned: formInput.DetectiveInvestigatorAssigned || false,
        Name: formInput.NotificationName || '',
        Phone: formInput.NotificationPhone || '',
        Emails: formInput.NotificationEmails || '',
        Agency: formInput.NotificationAgency || '',
        InvestigatorName: formInput.InvestigatorName || '',
        InvestigatorPhone: formInput.InvestigatorPhone || '',
        InvestigatorEmail: formInput.InvestigatorEmail || '',
        InvestigatorAgency: formInput.InvestigatorAgency || '',
        EmailsForNotifications: formInput.EmailsForNotifications || ''
      },
      // Include DeleteImage_IDs for edit mode to track images to be removed
      ...(isEditMode && images.some(img => img.isRemove) ? { DeleteImage_IDs: images.filter(img => img.isRemove).map(img => img.id) } : {})
    };
  }

  // Handle image removal - works for both existing and new images
  const handleRemoveImage = (imageIndex, id) => {

    if(!isNaN(id)) {
      
      // also add standardizedImageId to arrImageIDs_toRemove if exists
      const imgToRemove = images.find(img => img.id === id);
      if (imgToRemove && imgToRemove.standardizedImageId) {
        setArrImageIDs_toRemove(prev => [...prev, imgToRemove.standardizedImageId]);
      }

      setArrImageIDs_toRemove(prev => [...prev, id]);
    }

    setImages(prev => prev.map((img, index) => {
      if (index === imageIndex) {
        if (img.isExisting) {
          // Mark existing image for removal
          return { ...img, isRemove: true };
        } else {
          // For new images, we'll filter them out below
          return null;
        }
      }
      return img;
    }).filter(img => img !== null)); // Remove null entries (new images)

    // Adjust preview index if necessary
    const activeImages = images.filter(img => !img.isRemove);
    if (previewImageIndex >= activeImages.length - 1) {
      setPreviewImageIndex(Math.max(0, activeImages.length - 2));
    }
  };

  return (
    <>
      <div className={`form-import ${isModal ? 'modal-import' : ''}`}>
        <div className="form-import-title">
          {isEditMode ?
            (
              <>
                <PageTitle
                  title={language.import?.editImpressionEvidence || 'Edit Evidence'}
                  subtitle={language.import?.editImpressionEvidenceSub || 'Modify evidence images, crop (standardize) them, and update case details'}
                  icon={<i className="fa fa-pencil" aria-hidden="true"></i>}
                />
              </>
            )
            : (
              <>
                <PageTitle
                  title={language.import?.importImpressionEvidence || 'Import Evidence'}
                  subtitle={language.import?.importImpressionEvidenceSub || 'Upload evidence images, crop (standardize) them, and add case details'}
                  icon={svgIconImport}
                />
              </>
            )
          }
          {isModal && onClose && (
            <button className="modal-close-btn" onClick={onClose} type="button">
              <i className="fa fa-times"></i>
            </button>
          )}
          {isLoading && (
            <div className="loading-indicator">
              <i className="fa fa-spinner fa-spin"></i>
              <span>{language.import?.loadingGalleryData || "Loading gallery data..."}</span>
            </div>
          )}
        </div>
        <div className="form-import-content">
          <ImportImages
            images={images.filter(img => !img.isRemove)}
            setImages={memoizedSetImages}
            updateImageAtIndex={(index, newImage) => {
              setImages(prev => prev.map((img, i) => i === index ? newImage : img));
            }}
            onRemoveImage={handleRemoveImage}
            files={files}
            setFiles={setFiles}
            previewImageIndex={previewImageIndex}
            setPreviewImageIndex={setPreviewImageIndex}
            isEditMode={isEditMode}
          />
          <ImportMeta
            formInput={formInput}
            setFormInput={memoizedSetFormInput}
            defineCategory={defineCategory}
            onImportClick={ImportGallery}
            onResetClick={() => {
              // Reset only Impression Evidence Information, keep ImportImages and Scanner Information
              setFormInput(prev => {
                // Get current user information from localStorage to preserve Scanner Information
                let userData = {};
                try {
                  const userDataString = localStorage.getItem("FWUser");
                  if (userDataString) {
                    const parsedData = JSON.parse(userDataString);
                    const userInfo = parsedData.Information;
                    if (userInfo) {
                      userData = {
                        EnteredBy: userInfo.Name || '',
                        Phone: userInfo.Phone || '',
                        Email: userInfo.Email || '',
                        Agency: userInfo.AgencyName || ''
                      };
                    }
                  }
                } catch (error) {
                  console.error('Error loading user information:', error);
                }

                return {
                  // Keep system timestamps
                  CollectUtcTicks: prev.CollectUtcTicks,
                  CollectLocalTicks: prev.CollectLocalTicks,
                  TimeZone: prev.TimeZone,
                  TimeZoneOffset: prev.TimeZoneOffset,

                  // Keep Product Information (for TestShoe category)
                  Make: prev.Make,
                  Model: prev.Model,
                  ShoeType: prev.ShoeType,
                  Category: prev.Category,
                  ProductCode: prev.ProductCode,
                  Color: prev.Color,
                  ReferenceLink: prev.ReferenceLink,
                  Make_ID: prev.Make_ID,
                  Model_ID: prev.Model_ID,
                  ShoeType_ID: prev.ShoeType_ID,
                  EventType: prev.EventType,

                  // Keep Scanner Information
                  ...userData,

                  // Reset only Impression Evidence Information
                  CaseIncident: '',
                  CartridgeCaseNumber: '',
                  Country: '',
                  State: '',
                  County: '',
                  RecoveryEvent: '',
                  LocationIncident: '',
                  LocationNote: '',
                  Latitude: '',
                  Longitude: '',
                  DateOfIncident: null
                };
              });
            }}
            isImporting={isImporting}
            isMainLoading={isLoading}
            isEditMode={isEditMode}
          />
        </div>
        {/* <div className="form-import-buttons">
          <button
            className={`btn btn-success ${isImporting ? 'importing' : ''}`}
            id="btnImport"
            type="button"
            onClick={ImportGallery}
            disabled={isImporting || isLoading}
          >
            {isImporting ? (
              <>
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                {isEditMode ? (language.import?.updating || 'Updating...') : (language.import?.importing || 'Importing...')}
              </>
            ) : (
              <>
                <i className={`fa ${isEditMode ? 'fa-save' : 'fa-plus'}`} aria-hidden="true"></i>
                {isEditMode ? (language.import?.update || 'Update') : (language.import?.import || 'Import')}
              </>
            )}
          </button>
        </div> */}
      </div>
    </>
  )
};

export default Import;